import { createFileRoute, Link } from "@tanstack/react-router";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { categories, locations } from "@/lib/data";
import { CheckCircle2, Zap, Eye, BarChart3 } from "lucide-react";

const SITE_URL = "https://letslistbusiness.com";

export const Route = createFileRoute("/add-business/")({
  head: () => ({
    meta: [
      { title: "Add Your Business — Free Local Listing | LetsListBusiness" },
      { name: "description", content: "Reach customers actively searching for what you offer. Add your business to LetsListBusiness in under 2 minutes — free." },
      { property: "og:title", content: "Add Your Business to LetsListBusiness" },
      { property: "og:description", content: "Free local business listing." },
    ],
    links: [{ rel: "canonical", href: SITE_URL + "/add-business/" }],
  }),
  component: AddBusinessPage,
});

function AddBusinessPage() {
  return (
    <>
      <Breadcrumbs items={[{ label: "Home", href: "/" }, { label: "Add a business" }]} />

      <section className="container mx-auto grid gap-10 px-4 pb-16 lg:grid-cols-5">
        {/* Left — pitch */}
        <aside className="lg:col-span-2">
          <div className="sticky top-24">
            <div className="text-xs font-semibold uppercase tracking-[0.3em] text-primary">For business owners</div>
            <h1 className="mt-4 font-display text-4xl font-bold leading-tight sm:text-5xl">
              Get found by neighbors who are <em className="not-italic text-primary">already looking.</em>
            </h1>
            <p className="mt-5 text-muted-foreground">
              List your business in under 2 minutes. Free forever. No credit card.
            </p>

            <ul className="mt-10 space-y-5">
              {[
                { icon: Eye, title: "Real visibility", text: "120K+ neighbors browse LetsListBusiness every month." },
                { icon: Zap, title: "Live in minutes", text: "Submit your listing and we'll publish within 24 hours." },
                { icon: BarChart3, title: "Performance insights", text: "See views, clicks and reviews from your dashboard." },
                { icon: CheckCircle2, title: "Verified badge", text: "Stand out with our community-trusted verified mark." },
              ].map((b) => (
                <li key={b.title} className="flex gap-4">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-primary/10 text-primary">
                    <b.icon className="h-5 w-5" />
                  </span>
                  <div>
                    <div className="font-display text-lg font-semibold">{b.title}</div>
                    <div className="text-sm text-muted-foreground">{b.text}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        {/* Right — form */}
        <div className="lg:col-span-3">
          <form className="space-y-6 rounded-3xl border border-border bg-card p-6 shadow-card sm:p-10">
            <div className="flex items-center justify-between border-b border-border pb-4">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-primary">Step 1 of 1</div>
                <h2 className="mt-1 font-display text-2xl font-bold">Tell us about your business</h2>
              </div>
              <span className="rounded-full bg-success/15 px-3 py-1 text-xs font-semibold text-success">Free</span>
            </div>

            <Field label="Business name"><input className="field" placeholder="e.g. The Copper Fork" /></Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Category">
                <select className="field">
                  {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                </select>
              </Field>
              <Field label="City">
                <select className="field">
                  {locations.map((l) => <option key={l.slug} value={l.slug}>{l.name}, {l.state}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Address"><input className="field" placeholder="Street address" /></Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Phone"><input className="field" placeholder="(555) 123-4567" /></Field>
              <Field label="Website"><input className="field" placeholder="https://" /></Field>
            </div>
            <Field label="Short description"><textarea className="field min-h-[120px]" placeholder="Tell neighbors what makes your business special." /></Field>

            <div className="border-t border-border pt-4">
              <button type="button" className="w-full rounded-full bg-primary px-6 py-3.5 text-sm font-semibold text-primary-foreground hover:opacity-95">
                Submit listing →
              </button>
              <p className="mt-3 text-center text-xs text-muted-foreground">
                Already have an account? <Link to="/login" className="text-primary underline">Log in</Link>
              </p>
            </div>
          </form>
        </div>
      </section>
      <style>{`.field{width:100%;border-radius:.625rem;border:1px solid var(--border);background:var(--background);padding:.7rem .9rem;font-size:.875rem;outline:none;transition:all .15s}.field:focus{border-color:var(--ring);box-shadow:0 0 0 3px color-mix(in oklab, var(--ring) 25%, transparent)}`}</style>
    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
